import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import helpContentData from '../data/helpContent.json';

/**
 * Custom hook to manage help content and navigation
 * Provides access to help data, handles deep linking, and manages active section
 */
export const useHelpContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState(null);

  // Get the hash from URL (without the #)
  const hash = location.hash.slice(1);

  // Load help content
  const helpContent = useMemo(() => helpContentData, []);

  // Get all sections with their parent guide info for navigation
  const allSections = useMemo(() => {
    const sections = [];
    
    // Add all page guide sections
    helpContent.pageGuides.forEach((guide) => {
      guide.sections.forEach((section) => {
        sections.push({
          id: `${guide.id}-${section.id}`,
          title: section.title,
          content: section.content,
          parentId: guide.id,
          parentTitle: guide.title,
          type: 'guide',
        });
      });
    });

    // Add all lexicon terms
    helpContent.lexicon.forEach((term) => {
      sections.push({
        id: term.id,
        title: term.term,
        fullTitle: term.fullTerm,
        content: term.definition,
        relatedTerms: term.relatedTerms,
        type: 'lexicon',
      });
    });

    return sections;
  }, [helpContent]);

  // Filter sections based on search query
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) {
      return allSections;
    }

    const query = searchQuery.toLowerCase();
    return allSections.filter((section) => {
      const titleMatch = section.title.toLowerCase().includes(query);
      const parentMatch = section.parentTitle?.toLowerCase().includes(query);
      const contentMatch = section.content.toLowerCase().includes(query);
      const fullTitleMatch = section.fullTitle?.toLowerCase().includes(query);
      
      return titleMatch || parentMatch || contentMatch || fullTitleMatch;
    });
  }, [allSections, searchQuery]);

  // Get a specific section by ID
  const getSectionById = useCallback(
    (sectionId) => {
      return allSections.find((section) => section.id === sectionId);
    },
    [allSections]
  );

  // Navigate to a specific section
  const navigateToSection = useCallback(
    (sectionId) => {
      navigate(`/help#${sectionId}`);
      // Scroll to section after navigation
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    },
    [navigate]
  );

  // Get related terms for a lexicon entry
  const getRelatedTerms = useCallback(
    (termId) => {
      const term = helpContent.lexicon.find((t) => t.id === termId);
      if (!term || !term.relatedTerms) return [];

      return term.relatedTerms
        .map((relatedId) =>
          helpContent.lexicon.find((t) => t.id === relatedId)
        )
        .filter(Boolean);
    },
    [helpContent]
  );

  // Set active section based on hash
  useEffect(() => {
    if (hash) {
      setActiveSection(hash);
      // Scroll to section when hash changes
      setTimeout(() => {
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      setActiveSection(null);
    }
  }, [hash]);

  // Handle scroll to update active section in URL
  const handleScroll = useCallback(() => {
    const sections = document.querySelectorAll('[data-section-id]');
    let currentSection = null;

    sections.forEach((section) => {
      const rect = section.getBoundingClientRect();
      // Consider a section active if it's in the top 30% of viewport
      if (rect.top <= window.innerHeight * 0.3 && rect.bottom > 0) {
        currentSection = section.getAttribute('data-section-id');
      }
    });

    if (currentSection && currentSection !== hash) {
      // Update URL without scrolling
      window.history.replaceState(null, '', `/help#${currentSection}`);
    }
  }, [hash]);

  return {
    pageGuides: helpContent.pageGuides,
    lexicon: helpContent.lexicon,
    allSections,
    filteredSections,
    searchQuery,
    setSearchQuery,
    activeSection,
    setActiveSection,
    getSectionById,
    navigateToSection,
    getRelatedTerms,
    handleScroll,
  };
};
